import type { components } from '@/api'
import type { Field } from '@/modules/Project/Fields/types'
import { invariant } from '@/shared/utils/typeAssertions'
import { put } from './client'
import { PropertyType } from './types'

export type SetFieldValuePath = `/api/workspaces/${string}/projects/${string}/entities/${string}`
export type SetFieldValueRequest = components['schemas']['update_entity']

export type UpdateEntityFieldValue = Extract<
  components['schemas']['field_value'],
  Record<string, unknown> | null
>

export type PutEntityResponse = components['schemas']['Projects.Entities.EntityResponse']

export type SetFieldValueParams = {
  workspaceId: string
  projectId: string
  entityId: string
  // Mapping of property ID to new value
  fields: Record<string, UpdateEntityFieldValue>
}

/**
 * Updates the field values for an entity. Can update any number of fields
 * for the entity.
 */
export const setFieldValue = ({
  workspaceId,
  projectId,
  entityId,
  fields,
}: SetFieldValueParams) => {
  return put<SetFieldValueRequest, PutEntityResponse, SetFieldValuePath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/${entityId}`,
    { fields },
  )
}

/** Gets a payload for this endpoint from a field type and value */
export const getSetFieldValuePayload = (
  fieldType: PropertyType,
  manualValue: Field['manualValue'],
): UpdateEntityFieldValue => {
  switch (fieldType) {
    case PropertyType.reference:
      invariant(
        typeof manualValue === 'object' && !Array.isArray(manualValue),
        'manualValue is not a reference',
      )
      return {
        reference: manualValue,
      }
    case PropertyType.single_select:
    case PropertyType.multi_select:
    case PropertyType.user_select:
    case PropertyType.file_collection:
    case PropertyType.collection: {
      invariant(Array.isArray(manualValue), 'manualValue is not an array')
      return {
        options: manualValue,
      }
    }
    case PropertyType.data:
    case PropertyType.file: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { file_url: manualValue }
    }
    case PropertyType.json: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { json: manualValue }
    }
    case PropertyType.url: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { url: manualValue }
    }
    case PropertyType.text: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { text: manualValue }
    }
    case PropertyType.number: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { text: manualValue }
    }
  }
}
